import { useState, useEffect } from 'react';
import { Group, Paper, Stack, Text } from '@mantine/core';
import { useSalesPlanRepository } from '../../rpc';
import logger from '@ayocore/platform/common/helpers/logger';

const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.28)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: 'none',
} as const;

interface QuarterStats {
  branchPlan: number;
  branchFact: number;
  userPlan: number;
  userFact: number;
  quarterLabel: string;
}

interface WelcomeStats {
  branchName: string | null;
  current: QuarterStats;
  previous: QuarterStats;
}

/** Колонка план/факт за один квартал */
function QuarterColumn({ label, plan, fact }: { label: string; plan: number; fact: number }) {
  return (
    <Stack
      gap={2}
      style={{
        flex: 1,
        minWidth: 0,
        backgroundColor: '#ffffff5e',
        padding: '6px 8px',
        borderRadius: 8,
      }}
    >
      <Text size='xs' c='dimmed' ta='right'>
        {label}
      </Text>
      <Group gap='md'>
        <Stack gap={0}>
          <Text size='xs' c='dimmed'>
            План
          </Text>
          <Text size='sm' fw={600}>
            {fmt.format(plan)} ₽
          </Text>
        </Stack>
        <Stack gap={0}>
          <Text size='xs' c='dimmed'>
            Факт
          </Text>
          <Text size='sm' fw={600} c={fact > 0 ? 'green.7' : undefined}>
            {fmt.format(fact)} ₽
          </Text>
        </Stack>
      </Group>
    </Stack>
  );
}

/**
 * Блок план/факт филиала и пользователя за текущий и предыдущий квартал.
 */
export function SalesPlanWelcomeBlock() {
  const salesPlanRepository = useSalesPlanRepository();
  const [stats, setStats] = useState<WelcomeStats | null>(null);

  useEffect(() => {
    salesPlanRepository
      .getWelcomeStats()
      .then(data => {
        const result = data as WelcomeStats;
        if (result.current.quarterLabel) setStats(result);
      })
      .catch(err => logger.error('Failed to load welcome stats:', err));
  }, []);

  if (!stats) return null;

  return (
    <Group gap='md' grow>
      <Paper p='md' radius='lg' style={glassStyle}>
        <Stack gap='sm'>
          <Text size='xs' fw={500}>
            {stats.branchName ?? 'Филиал'}
          </Text>
          <Group gap='xl' wrap='nowrap'>
            <QuarterColumn
              label={stats.previous.quarterLabel}
              plan={stats.previous.branchPlan}
              fact={stats.previous.branchFact}
            />
            <QuarterColumn
              label={stats.current.quarterLabel}
              plan={stats.current.branchPlan}
              fact={stats.current.branchFact}
            />
          </Group>
        </Stack>
      </Paper>

      <Paper p='md' radius='lg' style={glassStyle}>
        <Stack gap='sm'>
          <Text size='xs' fw={500}>
            Мой план
          </Text>
          <Group gap='xl' wrap='nowrap'>
            <QuarterColumn
              label={stats.previous.quarterLabel}
              plan={stats.previous.userPlan}
              fact={stats.previous.userFact}
            />
            <QuarterColumn
              label={stats.current.quarterLabel}
              plan={stats.current.userPlan}
              fact={stats.current.userFact}
            />
          </Group>
        </Stack>
      </Paper>
    </Group>
  );
}
